import {ChangeDetectionStrategy, Component, inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {Observable, of} from "rxjs";
import {AlgorithmicDmvActions} from "../store/algorithmic-dmv.actions";
import {AlgorithmicDmvAgentMode, AlgorithmicDmvJobInfo, AlgorithmicDmvOverwriteMode} from "../generated-sources/api";
import {selectActiveJobForFundValuation, selectReadyJobForFundValuation} from "../store/algorithmic-dmv.selectors";

/** Source of the existing DMV on an asset, as seen from the dialog's perspective. */
export type ExistingDmvSource = "NONE" | "ALGORITHM" | "USER";

export interface AlgorithmicDmvAssetRow {
    id: number;
    name: string;
    /** "NONE" → no DMV row exists yet on this (asset, fundValuation) pair. */
    existingDmvSource: ExistingDmvSource;
    /**
     * Eligible to be processed by the algorithm. False matches the backend's
     * INELIGIBLE result reason (e.g. no DMV yet, asset realised, multi-exit modelled).
     */
    isEligible: boolean;
    /** Human-readable reason when {@link isEligible} is false. */
    ineligibilityReason?: string;
    /** Current reported NAV — position context shown on the row. */
    nav?: number;
    /** Current unrealized TVPI (factor). */
    unrealizedTvpi?: number;
    /** Current total TVPI (factor). */
    totalTvpi?: number;
}

export type GpQuality = "LOW" | "MEDIUM" | "HIGH";

export interface AlgorithmicDmvDialogData {
    fundId: number;
    /**
     * Bottom-Up fund valuation to scope the batch to.
     * Null triggers the prereq empty-state — the dialog closes with
     * {@link AlgorithmicDmvDialogResult.CreateValuation} and the caller is
     * expected to open the create-valuation flow then re-open this dialog
     * with the new id.
     */
    fundValuationId: number | null;
    assets: AlgorithmicDmvAssetRow[];
    /** Whether the fund has a general partner at all. Drives where (if anywhere) the chosen quality is saved. */
    hasGp: boolean;
    /** Current GP quality for the fund's general partner (null if no GP, or GP has none set). */
    gpQuality: GpQuality | null;
    /**
     * Pre-fill for the "Expected gross IRR" field, as a percentage (e.g. 25 for 25%), resolved from
     * the {@code asset.requiredGrossIRR.default} setting by the caller. Undefined when the setting is
     * unset/not yet loaded — the field then falls back to 25%. The user can always override it.
     */
    defaultGrossIrrPercent?: number;
}

export const ALGORITHMIC_DMV_DIALOG_RESULT_CREATE_VALUATION = "CREATE_VALUATION" as const;

/** Dialog result: request creating a new valuation, or nothing. */
export type AlgorithmicDmvDialogResult =
    | typeof ALGORITHMIC_DMV_DIALOG_RESULT_CREATE_VALUATION
    | undefined;

interface OverwriteModeOption {
    value: AlgorithmicDmvOverwriteMode;
    label: string;
    description: string;
}

@Component({
    selector: "valumize-algorithmic-dmv-dialog",
    templateUrl: "./algorithmic-dmv-dialog.component.html",
    styleUrls: ["./algorithmic-dmv-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AlgorithmicDmvDialogComponent {

    readonly data = inject<AlgorithmicDmvDialogData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<AlgorithmicDmvDialogComponent, AlgorithmicDmvDialogResult>>(MatDialogRef);
    private readonly fb = inject(FormBuilder);
    private readonly store = inject(Store);

    readonly overwriteModes: OverwriteModeOption[] = [
        {
            value: AlgorithmicDmvOverwriteMode.SkipExisting,
            label: "Skip existing",
            description: "Only run where no DMV exists yet for this asset in this fund valuation."
        },
        {
            value: AlgorithmicDmvOverwriteMode.OverwriteAlgorithm,
            label: "Overwrite algorithm-sourced",
            description: "Re-run where the existing DMV was set by the algorithm. Keep user-edited DMVs."
        },
        {
            value: AlgorithmicDmvOverwriteMode.OverwriteAll,
            label: "Overwrite all",
            description: "Re-run on every selected asset, overwriting user edits."
        }
    ];

    /** Exposes the agent-mode enum to the template for the per-row segmented toggle. */
    readonly AGENT_MODE = AlgorithmicDmvAgentMode;

    /**
     * Per-asset agent-review depth, keyed by asset id. Component state (not a form
     * control) — the segmented toggle on each row writes here; submit() reads it for
     * the selected assets. Absent → {@link AlgorithmicDmvAgentMode.Off}.
     */
    private agentModeByAsset = new Map<number, AlgorithmicDmvAgentMode>(
        this.data.assets.map(a => [a.id, AlgorithmicDmvAgentMode.Off] as [number, AlgorithmicDmvAgentMode])
    );

    readonly hasValuation = this.data.fundValuationId !== null;

    readonly gpQualities: GpQuality[] = ["LOW", "MEDIUM", "HIGH"];

    readonly form = this.fb.group({
        // Default selection = every eligible asset whose existing DMV is algorithm-sourced
        // or absent. We do NOT pre-select USER-sourced rows because the default mode is
        // OVERWRITE_ALGORITHM and selecting them would just yield no-op skips on the
        // server. The user can still tick them and switch mode if they want.
        assetIds: this.fb.control<number[]>(
            this.data.assets
                .filter(a => a.isEligible && a.existingDmvSource !== "USER")
                .map(a => a.id),
            Validators.required
        ),
        overwriteMode: this.fb.control<AlgorithmicDmvOverwriteMode>(
            AlgorithmicDmvOverwriteMode.OverwriteAlgorithm,
            Validators.required
        ),
        // GP quality — pre-filled from the GP; required so the user makes a conscious
        // choice (the algorithm otherwise silently assumes MEDIUM). Saved back to the GP.
        gpQuality: this.fb.control<GpQuality | null>(this.data.gpQuality, Validators.required),
        // Expected gross IRR as a percentage, applied to every selected asset for the run.
        // Pre-filled from the asset.requiredGrossIRR.default setting (falling back to 25% when unset);
        // required (min > 0) so we never send an empty rate. The user can override it.
        gpIrrPercent: this.fb.control<number>(this.data.defaultGrossIrrPercent ?? 25, [Validators.required, Validators.min(0.01)])
    });

    /** Whether the fund has a general partner — when false the chosen quality is run-only. */
    readonly hasGp = this.data.hasGp;

    /** True when a GP exists but had no quality set on open — drives the "saved onto GP" warning. */
    readonly gpQualityWasUnset = this.data.hasGp && this.data.gpQuality == null;

    /**
     * An in-flight run for THIS fund valuation. When present the Run button is disabled
     * (the backend enforces one active run per fund valuation and would reject with 409).
     */
    readonly activeJobForValuation$: Observable<AlgorithmicDmvJobInfo | undefined> =
        this.data.fundValuationId !== null
            ? this.store.select(selectActiveJobForFundValuation(this.data.fundId, this.data.fundValuationId))
            : of(undefined);

    /**
     * A completed run for THIS fund valuation still awaiting review. Starting a new run
     * supersedes (auto-skips) those un-reviewed results, so we warn the user first.
     */
    readonly readyJobForValuation$: Observable<AlgorithmicDmvJobInfo | undefined> =
        this.data.fundValuationId !== null
            ? this.store.select(selectReadyJobForFundValuation(this.data.fundId, this.data.fundValuationId))
            : of(undefined);

    constructor() {
        // Refresh the fund's jobs so the same-valuation warning reflects the latest state.
        // Only relevant once a valuation is chosen (the empty-state has nothing to warn about).
        if (this.data.fundValuationId !== null) {
            this.store.dispatch(AlgorithmicDmvActions.loadJobs({fundId: this.data.fundId}));
        }
    }

    selectEligible() {
        this.form.controls.assetIds.setValue(
            this.data.assets.filter(a => a.isEligible).map(a => a.id)
        );
    }

    selectAll() {
        // Scope to eligible assets only — ineligible rows have a disabled checkbox and the
        // backend rejects them as INELIGIBLE, so "select all" must never put them in scope.
        this.form.controls.assetIds.setValue(
            this.data.assets.filter(a => a.isEligible).map(a => a.id)
        );
    }

    clearAll() {
        this.form.controls.assetIds.setValue([]);
    }

    isAssetSelected(assetId: number): boolean {
        return (this.form.controls.assetIds.value ?? []).includes(assetId);
    }

    toggleAsset(assetId: number, checked: boolean) {
        const current = this.form.controls.assetIds.value ?? [];
        const next = checked ? [...current, assetId] : current.filter(id => id !== assetId);
        this.form.controls.assetIds.setValue(next);
    }

    /** Current agent-review depth chosen for an asset (Off when none picked yet). */
    modeFor(assetId: number): AlgorithmicDmvAgentMode {
        return this.agentModeByAsset.get(assetId) ?? AlgorithmicDmvAgentMode.Off;
    }

    /** Set the agent-review depth for a single asset (reassigns the map for OnPush). */
    setAgentMode(assetId: number, mode: AlgorithmicDmvAgentMode) {
        this.agentModeByAsset = new Map(this.agentModeByAsset).set(assetId, mode);
    }

    /** Bulk-apply an agent-review depth to every asset currently in scope. */
    setAllSelectedAgentMode(mode: AlgorithmicDmvAgentMode) {
        const selected = this.form.controls.assetIds.value ?? [];
        const next = new Map(this.agentModeByAsset);
        selected.forEach(id => next.set(id, mode));
        this.agentModeByAsset = next;
    }

    /** Count of in-scope assets that will get an agent review (Quick or Deep). */
    get reviewedCount(): number {
        return (this.form.controls.assetIds.value ?? [])
            .filter(id => this.modeFor(id) !== AlgorithmicDmvAgentMode.Off).length;
    }

    requestCreateValuation() {
        this.dialogRef.close(ALGORITHMIC_DMV_DIALOG_RESULT_CREATE_VALUATION);
    }

    submit() {
        if (this.form.invalid || this.data.fundValuationId === null) return;
        const v = this.form.value;
        // Pair each in-scope asset with its chosen agent-review depth. The completion
        // notification's deep link is built server-side from the job's fund valuation, so
        // the dialog no longer supplies a returnUrl.
        const assetReviews = (v.assetIds ?? []).map(assetId => ({
            assetId,
            agentMode: this.modeFor(assetId)
        }));
        this.store.dispatch(AlgorithmicDmvActions.start({
            fundId: this.data.fundId,
            request: {
                fundValuationId: this.data.fundValuationId,
                assetReviews,
                overwriteMode: v.overwriteMode!,
                gpQuality: v.gpQuality!,
                // Dialog captures the rate as a percentage; the API expects a fraction.
                requiredGrossIrr: v.gpIrrPercent! / 100
            }
        }));
        this.dialogRef.close();
    }

    cancel() {
        this.dialogRef.close();
    }
}
